import { useState, useEffect } from "react";
import {
    Printer,
    Translate,
    CaretRight,
    DownloadSimple,
    PlayCircle,
    Sparkle,
    Image as ImgIcon,
    FileXls,
} from "@phosphor-icons/react";
import * as XLSX from "xlsx";

/* ---------------- Bilingual content (Hinglish for Hindi) ---------------- */
const CONTENT = {
    en: {
        title: "How to Make an ID Card — Easy 5-Minute Guide",
        subtitle:
            "Make 1 card or 1000 cards in minutes. No design skills needed.",
        printBtn: "Print / Save as PDF",
        sampleBtn: "Download Sample Excel",
        langBtn: "हिंदी में पढ़ें",
        before: {
            h: "📦 What you need",
            items: [
                "💻 A laptop or desktop with Chrome / Edge browser",
                "📧 Your email (for free signup)",
                "📊 (Optional) Excel file with student / employee names — sample below ⬇",
                "📷 (Optional) One photo per person (passport size, JPG or PNG)",
            ],
        },
        steps: [
            {
                n: 1,
                t: "Sign up (1 minute)",
                d: "Go to idcardgen.com → click Get Started → enter Name, Email, Mobile, Password → Done. You get a Free Trial automatically with watermark.",
                tip: "No credit card needed for trial.",
            },
            {
                n: 2,
                t: "Create your first card project",
                d: "On the Dashboard click + New Template → give it a name (e.g. 'School ID 2026') → click Create. The Studio editor opens.",
                tip: null,
            },
            {
                n: 3,
                t: "Design the card (drag & drop)",
                d: "Left toolbar has: T = Text, 👤 = Photo, ✏️ = Signature, ▭ = Rectangle, ⬛ = QR, ▮▮ = Barcode. Click any tool to add it. Drag with the mouse. The selected element shows a violet rotation handle on top — drag to rotate. Use the right panel to change font, color, size.",
                tip: "Arrow keys nudge by 1px. Shift + arrow = 10px.",
            },
            {
                n: 4,
                t: "Add data — Single OR Bulk",
                d: "ONE card: type directly into text boxes, click the Photo box to upload. MANY cards: scroll the right panel → Data Sheet → click 'Sample' (green link) to download our ready Excel → fill your data → use 'Upload Excel'. Then upload all photos with 'Student Photos → Upload'.",
                tip: "See the Photo Linking section below — most important step!",
            },
            {
                n: 5,
                t: "Save → Export → Print",
                d: "Click 💾 Save (top right). Then PDF (best for print shops), PNG (for WhatsApp share), or Print (direct printer). The file downloads automatically.",
                tip: null,
            },
        ],
        photoLink: {
            h: "📷 Photo Linking — How photos go on cards",
            intro: "This is the trick that makes bulk printing magical. Just 3 simple rules:",
            rules: [
                {
                    n: 1,
                    t: "Add a 'Photo' column in Excel",
                    d: "In Excel, make a column named 'Photo'. In each row, write the file name of that student's photo. Example: 23.jpg, ravi.png, REG2026001.jpg",
                },
                {
                    n: 2,
                    t: "Save photos with the SAME name",
                    d: "Each photo file name must EXACTLY match the Excel value. If Excel says 23.jpg → file must be 23.jpg (not 23.jpeg, not Photo23.jpg).",
                },
                {
                    n: 3,
                    t: "Upload all photos together",
                    d: "Studio → Student Photos → Upload → select ALL photo files at once (Ctrl+A in the file picker). The system auto-matches by name.",
                },
            ],
            bind: "Then on the card: select the Photo box → 'Bind to column' → pick 'Photo'. Done — every card now shows the right student's face.",
        },
        excel: {
            h: "📊 Sample Excel — Download & Edit",
            sub: "Click the green button to download a ready Excel with 3 sample rows + an Instructions sheet inside.",
            cols: ["Photo", "StudentName", "Class", "RegNo", "DOB"],
            rows: [
                ["23.jpg", "Ravi Kumar", "10-A", "REG2026001", "2010-04-15"],
                ["24.jpg", "Priya Singh", "10-A", "REG2026002", "2010-07-08"],
                ["25.jpg", "Arjun Sharma", "10-B", "REG2026003", "2010-02-22"],
            ],
        },
        trouble: {
            h: "🔧 Common Problems & Fix",
            rows: [
                [
                    "Photos not showing on cards",
                    "Excel 'Photo' column value must EXACTLY match the photo file name. Cell = 23.jpg → file must be named 23.jpg.",
                ],
                [
                    "Watermark on card",
                    "You're on Free Trial. Open the Pricing page and upgrade.",
                ],
                [
                    "Excel upload error",
                    "Save your file as .xlsx (NOT .xls). Maximum 5000 rows allowed.",
                ],
                [
                    "Forgot password",
                    "Login page → click 'Forgot password?' → check your email.",
                ],
                [
                    "PDF blank or 0 KB",
                    "Add at least one element on the canvas before exporting. Refresh if still blank.",
                ],
                [
                    "Hindi text not showing properly",
                    "In the Font Family dropdown choose 'Noto Sans Devanagari', 'Hind' or 'Mukta'.",
                ],
            ],
        },
        support: "💬 Need more help?",
    },
    hi: {
        // Hinglish — Roman script Hindi mixed with English keywords
        title: "ID Card Kaise Banayein — 5 Minute ki Easy Guide",
        subtitle:
            "1 card ho ya 1000 card — sirf minutes mein ban jayenge. Designing ki tension nahi.",
        printBtn: "Print / PDF Save Karein",
        sampleBtn: "Sample Excel Download",
        langBtn: "Read in English",
        before: {
            h: "📦 Aapko kya chahiye",
            items: [
                "💻 Ek laptop ya desktop (Chrome ya Edge browser ke saath)",
                "📧 Apki email (free signup ke liye)",
                "📊 (Optional) Excel file jisme students ke naam ho — sample neeche download karein ⬇",
                "📷 (Optional) Har vyakti ka 1 photo (passport size, JPG ya PNG)",
            ],
        },
        steps: [
            {
                n: 1,
                t: "Sign up karein (1 minute)",
                d: "idcardgen.com par jayein → 'Get Started' click karein → Naam, Email, Mobile, Password bharein → Done. Free Trial automatic mil jayega (watermark ke saath).",
                tip: "Trial ke liye credit card ki zarurat NAHI hai.",
            },
            {
                n: 2,
                t: "Pehla card project banayein",
                d: "Dashboard par '+ New Template' click karein → naam dein (jaise 'School ID 2026') → 'Create' dabayein. Studio editor khul jayega.",
                tip: null,
            },
            {
                n: 3,
                t: "Card design karein (drag & drop)",
                d: "Left side mein tools hain: T = Text, 👤 = Photo, ✏️ = Signature, ▭ = Rectangle, ⬛ = QR, ▮▮ = Barcode. Tool click karke add karein, mouse se drag karein. Selected element ke upar violet round (rotate handle) aata hai — drag karke rotate karein. Right panel se font, color, size badlein.",
                tip: "Arrow keys se 1px move hota hai. Shift + arrow = 10px.",
            },
            {
                n: 4,
                t: "Data add karein — Single ya Bulk",
                d: "EK card: text box mein direct type karein, photo box click karke upload karein. BAHUT cards: right panel mein 'Data Sheet' → green 'Sample' link se ready Excel download karein → apna data bharein → 'Upload Excel' karein. Phir 'Student Photos → Upload' se sab photos upload karein.",
                tip: "Neeche 'Photo Linking' section zaroor padein — ye sabse important hai!",
            },
            {
                n: 5,
                t: "Save → Export → Print",
                d: "💾 Save click karein (top right). Phir PDF (print shop ke liye), PNG (WhatsApp share ke liye), ya Print (direct printer). File apne aap download ho jayegi.",
                tip: null,
            },
        ],
        photoLink: {
            h: "📷 Photo Linking — Photo card par kaise jata hai",
            intro: "Ye trick samajhna zaruri hai bulk printing ke liye. Bas 3 simple rules:",
            rules: [
                {
                    n: 1,
                    t: "Excel mein 'Photo' column banayein",
                    d: "Excel mein 'Photo' naam ka column banayein. Har row mein us student ki photo ka file name likhein. Example: 23.jpg, ravi.png, REG2026001.jpg",
                },
                {
                    n: 2,
                    t: "Photos ko EXACT us hi naam se save karein",
                    d: "Photo file ka naam Excel ke value se EXACT match hona chahiye. Excel mein '23.jpg' → file bhi 23.jpg hi (23.jpeg ya Photo23.jpg nahi).",
                },
                {
                    n: 3,
                    t: "Saari photos ek saath upload karein",
                    d: "Studio → 'Student Photos' → Upload → file picker mein Ctrl+A dabakar saari photos select karein → Open. System khud match kar lega.",
                },
            ],
            bind: "Phir card par: Photo box select karein → right panel mein 'Bind to column' → 'Photo' choose karein. Bas — har card par sahi student ki photo aa jayegi.",
        },
        excel: {
            h: "📊 Sample Excel — Download Karke Edit Karein",
            sub: "Green button dabakar ready Excel download karein — usme 3 sample rows aur ek 'Instructions' sheet hai jo sab samjhati hai.",
            cols: ["Photo", "StudentName", "Class", "RegNo", "DOB"],
            rows: [
                ["23.jpg", "Ravi Kumar", "10-A", "REG2026001", "2010-04-15"],
                ["24.jpg", "Priya Singh", "10-A", "REG2026002", "2010-07-08"],
                ["25.jpg", "Arjun Sharma", "10-B", "REG2026003", "2010-02-22"],
            ],
        },
        trouble: {
            h: "🔧 Common Problems aur Solution",
            rows: [
                [
                    "Cards par photos nahi aa rahe",
                    "Excel 'Photo' column ki value EXACT photo file name se match honi chahiye. Cell = 23.jpg → file ka naam bhi 23.jpg hi ho.",
                ],
                [
                    "Card par watermark dikh raha",
                    "Aap Free Trial par hain. Pricing page se upgrade karein.",
                ],
                [
                    "Excel upload mein error",
                    "File ko .xlsx mein save karein (.xls NAHI). Max 5000 rows allowed.",
                ],
                [
                    "Password bhul gaye",
                    "Login page → 'Forgot password?' click karein → email check karein.",
                ],
                [
                    "PDF blank ya 0 KB",
                    "Export se pehle canvas par kam se kam 1 element add karein. Page refresh karein agar phir bhi blank.",
                ],
                [
                    "Hindi text theek se nahi dikh raha",
                    "Font Family dropdown mein 'Noto Sans Devanagari', 'Hind' ya 'Mukta' choose karein.",
                ],
            ],
        },
        support: "💬 Aur madad chahiye?",
    },
};

/* ---------------- Component ---------------- */
export default function Help() {
    const [lang, setLang] = useState(
        () => localStorage.getItem("idcardgen_help_lang") || "en",
    );
    const c = CONTENT[lang];

    useEffect(() => {
        localStorage.setItem("idcardgen_help_lang", lang);
        document.title =
            lang === "hi"
                ? "ID Card Kaise Banayein — IDCardGen Help"
                : "How to Make an ID Card — IDCardGen Help";
        document.documentElement.lang = lang;
        const desc =
            lang === "hi"
                ? "School, college aur employee ID card banane ka asaan 5-step guide — Hinglish mein."
                : "Easy 5-step guide to design, bulk-import and print school, college and employee ID cards.";
        let m = document.querySelector('meta[name="description"]');
        if (!m) {
            m = document.createElement("meta");
            m.name = "description";
            document.head.appendChild(m);
        }
        m.setAttribute("content", desc);
        let canon = document.querySelector('link[rel="canonical"]');
        if (!canon) {
            canon = document.createElement("link");
            canon.rel = "canonical";
            document.head.appendChild(canon);
        }
        canon.href = "https://idcardgen.com/help";
    }, [lang]);

    const print = () => {
        setTimeout(() => {
            try {
                if (document.activeElement && document.activeElement.blur)
                    document.activeElement.blur();
            } catch {}
            window.print();
        }, 50);
    };

    const downloadSample = () => {
        const headers = [
            "Photo",
            "StudentName",
            "FatherName",
            "Class",
            "RegNo",
            "DOB",
            "Address",
            "BloodGroup",
            "Mobile",
        ];
        const rows = [
            [
                "23.jpg",
                "Ravi Kumar",
                "Suresh Kumar",
                "10-A",
                "REG2026001",
                "2010-04-15",
                "Patna, Bihar",
                "O+",
                "9876543210",
            ],
            [
                "24.jpg",
                "Priya Singh",
                "Ramesh Singh",
                "10-A",
                "REG2026002",
                "2010-07-08",
                "Patna, Bihar",
                "A+",
                "9876543211",
            ],
            [
                "25.jpg",
                "Arjun Sharma",
                "Vikas Sharma",
                "10-B",
                "REG2026003",
                "2010-02-22",
                "Patna, Bihar",
                "B+",
                "9876543212",
            ],
        ];
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        ws["!cols"] = headers.map(() => ({ wch: 16 }));

        const instructions =
            lang === "hi"
                ? [
                      ["📖 IDCardGen — Sample Excel — Hinglish Instructions"],
                      [""],
                      ["1️⃣ Har row = ek ID card."],
                      [
                          "2️⃣ Column ke naam (header row) Studio mein bindable fields ban jayenge.",
                      ],
                      [
                          "   Studio mein text element edit karke 'Bind to column' se select karein.",
                      ],
                      [""],
                      ["📷 PHOTO LINKING (sabse important)"],
                      [
                          "1. 'Photo' column mein har student ki photo file ka naam likhein. Jaise: 23.jpg, 24.png, ravi.jpg",
                      ],
                      [
                          "2. Studio → Student Photos → Upload se saari photos ek saath upload karein (Ctrl+A).",
                      ],
                      [
                          "3. File name EXACT match hona chahiye Excel value se (case-insensitive).",
                      ],
                      [
                          "4. Card par Photo box add karein → 'Bind to column' → 'Photo' choose karein.",
                      ],
                      [""],
                      ["⚠️ TIPS"],
                      ["• File .xlsx mein save karein (.xls nahi)."],
                      ["• Maximum 5000 rows."],
                      [
                          "• Header naam short rakhein, space na dein (RegNo likhein, 'Reg No' nahi).",
                      ],
                      [
                          "• QR / Barcode ko bhi column se bind kar sakte hain (jaise RegNo) — har card par unique code aayega.",
                      ],
                      [""],
                      ["🌐 Madad: https://idcardgen.com/help"],
                  ]
                : [
                      ["📖 IDCardGen — Sample Excel — Instructions"],
                      [""],
                      ["1️⃣ Each row = one ID card."],
                      [
                          "2️⃣ Column names (header row) become bindable fields in the Studio.",
                      ],
                      [
                          "   Edit a text element → select 'Bind to column' → pick the column.",
                      ],
                      [""],
                      ["📷 PHOTO LINKING (most important)"],
                      [
                          "1. In the 'Photo' column, write each student's photo file name. Example: 23.jpg, 24.png, ravi.jpg",
                      ],
                      [
                          "2. In Studio → Student Photos → Upload → select all photo files at once (Ctrl+A).",
                      ],
                      [
                          "3. File name must EXACTLY match the Excel value (case-insensitive).",
                      ],
                      [
                          "4. On the card, add a Photo box → 'Bind to column' → choose 'Photo'.",
                      ],
                      [""],
                      ["⚠️ TIPS"],
                      ["• Save file as .xlsx (not .xls)."],
                      ["• Maximum 5000 rows."],
                      [
                          "• Use short column names with no spaces (RegNo, not 'Reg No').",
                      ],
                      [
                          "• QR / Barcode can also be bound to a column (e.g. RegNo) for unique codes per card.",
                      ],
                      [""],
                      ["🌐 Help: https://idcardgen.com/help"],
                  ];
        const wsInfo = XLSX.utils.aoa_to_sheet(instructions);
        wsInfo["!cols"] = [{ wch: 100 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Data");
        XLSX.utils.book_append_sheet(wb, wsInfo, "Instructions");
        XLSX.writeFile(wb, "idcardgen-sample.xlsx");
    };

    const toggleLang = () => setLang(lang === "en" ? "hi" : "en");

    return (
        <>
            {/* Print stylesheet */}
            <style>{`
                @media print {
                    body * { visibility: hidden !important; }
                    .help-page, .help-page * { visibility: visible !important; }
                    .help-page { position: absolute !important; left: 0; top: 0; width: 100% !important; padding: 0 !important; max-width: 100% !important; }
                    .no-print { display: none !important; }
                    body { background: white !important; }
                    .help-card { box-shadow: none !important; border: 1px solid #ddd !important; page-break-inside: avoid; background: white !important; }
                    a { color: #1d4ed8 !important; text-decoration: underline !important; }
                    @page { size: A4; margin: 14mm 12mm; }
                }
            `}</style>

            <div
                className="help-page max-w-4xl mx-auto px-4 sm:px-6 py-8"
                lang={lang}
            >
                {/* Toolbar */}
                <div className="no-print flex flex-wrap items-center justify-between gap-3 mb-6">
                    <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono uppercase tracking-wider">
                        <span>idcardgen.com</span>
                        <CaretRight size={12} />
                        <span>help</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={toggleLang}
                            className="btn-ghost flex items-center gap-2"
                            data-testid="help-lang-toggle"
                        >
                            <Translate size={16} />
                            {c.langBtn}
                        </button>
                        <button
                            onClick={print}
                            className="btn-ghost flex items-center gap-2"
                            data-testid="help-print"
                        >
                            <Printer size={16} />
                            {c.printBtn}
                        </button>
                    </div>
                </div>

                {/* Header */}
                <header className="help-card border border-zinc-200 bg-gradient-to-br from-violet-100 via-pink-50 to-amber-50 p-6 sm:p-10 mb-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-300/20 rounded-full blur-3xl pointer-events-none" />
                    <div className="relative">
                        <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur px-3 py-1 text-[11px] font-mono uppercase tracking-widest text-violet-700 mb-3">
                            <Sparkle size={12} weight="fill" /> Easy Guide
                        </div>
                        <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-zinc-900 leading-tight">
                            {c.title}
                        </h1>
                        <p className="mt-3 text-zinc-700 text-base sm:text-lg max-w-2xl">
                            {c.subtitle}
                        </p>

                        {/* CTA buttons */}
                        <div className="no-print flex flex-wrap gap-3 mt-6">
                            <button
                                onClick={downloadSample}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-3 font-semibold flex items-center gap-2 shadow-lg shadow-emerald-500/30 transition"
                                data-testid="help-download-sample"
                            >
                                <DownloadSimple size={18} weight="bold" />{" "}
                                {c.sampleBtn}
                            </button>
                            <a
                                href="/register"
                                className="bg-violet-600 hover:bg-violet-700 text-white px-5 py-3 font-semibold flex items-center gap-2 shadow-lg shadow-violet-500/30 transition"
                            >
                                <PlayCircle size={18} weight="fill" />{" "}
                                {lang === "hi"
                                    ? "Abhi Start Karein"
                                    : "Start Now"}
                            </a>
                        </div>
                    </div>
                </header>

                {/* Before */}
                <section className="help-card border border-zinc-200 bg-white p-5 sm:p-6 mb-5">
                    <h2 className="text-lg font-bold text-zinc-900 mb-3">
                        {c.before.h}
                    </h2>
                    <ul className="space-y-2 text-sm text-zinc-700">
                        {c.before.items.map((it, i) => (
                            <li
                                key={i}
                                className="flex gap-2 items-start leading-relaxed"
                            >
                                <span className="text-violet-600 font-bold">
                                    ✓
                                </span>
                                <span>{it}</span>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* Steps */}
                <h2 className="font-display font-black text-2xl tracking-tight text-zinc-900 mb-3 mt-8">
                    {lang === "hi"
                        ? "📝 5 Steps mein Banayein"
                        : "📝 Make in 5 Steps"}
                </h2>
                {c.steps.map((s) => (
                    <section
                        key={s.n}
                        className="help-card border border-zinc-200 bg-white p-5 sm:p-6 mb-4 flex gap-4"
                    >
                        <div className="shrink-0 w-14 h-14 g-violet text-white font-extrabold text-2xl flex items-center justify-center shadow-lg shadow-violet-500/30">
                            {s.n}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg font-bold text-zinc-900">
                                {s.t}
                            </h3>
                            <p className="mt-2 text-sm text-zinc-700 leading-relaxed">
                                {s.d}
                            </p>
                            {s.tip && (
                                <div className="mt-3 text-xs bg-amber-50 border-l-4 border-amber-400 px-3 py-2 text-amber-900">
                                    💡 {s.tip}
                                </div>
                            )}
                        </div>
                    </section>
                ))}

                {/* Photo linking — special highlighted section */}
                <section className="help-card border-2 border-violet-300 bg-gradient-to-br from-violet-50 to-pink-50 p-5 sm:p-6 mb-6 mt-8">
                    <div className="flex items-center gap-2 mb-2">
                        <ImgIcon
                            size={24}
                            weight="duotone"
                            className="text-violet-700"
                        />
                        <h2 className="text-lg font-bold text-zinc-900">
                            {c.photoLink.h}
                        </h2>
                    </div>
                    <p className="text-sm text-zinc-700 mb-4">
                        {c.photoLink.intro}
                    </p>
                    <div className="grid sm:grid-cols-3 gap-3">
                        {c.photoLink.rules.map((r) => (
                            <div
                                key={r.n}
                                className="bg-white border border-violet-200 p-4 shadow-sm"
                            >
                                <div className="w-8 h-8 g-violet text-white font-extrabold flex items-center justify-center text-sm mb-2">
                                    {r.n}
                                </div>
                                <h4 className="font-bold text-sm text-zinc-900 mb-1">
                                    {r.t}
                                </h4>
                                <p className="text-xs text-zinc-600 leading-relaxed">
                                    {r.d}
                                </p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 bg-white border-l-4 border-emerald-500 px-3 py-2 text-sm text-zinc-800">
                        ✅ {c.photoLink.bind}
                    </div>

                    {/* Visual flow diagram */}
                    <div className="mt-4 bg-white border border-zinc-200 p-4 overflow-x-auto">
                        <div className="flex items-center justify-around gap-3 min-w-max text-center text-xs">
                            <div>
                                <div className="w-24 h-16 border-2 border-blue-300 bg-blue-50 flex flex-col items-center justify-center font-mono font-bold">
                                    <span className="text-[10px] text-blue-600">
                                        Excel cell
                                    </span>
                                    <span className="text-blue-700">
                                        23.jpg
                                    </span>
                                </div>
                            </div>
                            <div className="text-2xl text-zinc-400">→</div>
                            <div>
                                <div className="w-24 h-16 border-2 border-emerald-300 bg-emerald-50 flex flex-col items-center justify-center font-mono font-bold">
                                    <span className="text-[10px] text-emerald-600">
                                        photo file
                                    </span>
                                    <span className="text-emerald-700">
                                        23.jpg
                                    </span>
                                </div>
                            </div>
                            <div className="text-2xl text-zinc-400">→</div>
                            <div>
                                <div className="w-24 h-16 border-2 border-violet-300 bg-violet-50 flex flex-col items-center justify-center">
                                    <span className="text-2xl">🪪</span>
                                    <span className="text-[10px] text-violet-700 mt-1">
                                        {lang === "hi" ? "card par" : "on card"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Excel sample */}
                <section className="help-card border border-zinc-200 bg-white p-5 sm:p-6 mb-5">
                    <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                        <div>
                            <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                                <FileXls
                                    size={22}
                                    weight="duotone"
                                    className="text-emerald-600"
                                />
                                {c.excel.h}
                            </h2>
                            <p className="text-xs text-zinc-500 mt-1 max-w-xl">
                                {c.excel.sub}
                            </p>
                        </div>
                        <button
                            onClick={downloadSample}
                            className="no-print bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-2 flex items-center gap-1.5 shadow-md"
                        >
                            <DownloadSimple size={14} weight="bold" />{" "}
                            {c.sampleBtn}
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-zinc-200">
                            <thead className="bg-zinc-50">
                                <tr>
                                    {c.excel.cols.map((col) => (
                                        <th
                                            key={col}
                                            className="border border-zinc-200 px-3 py-2 text-left font-mono text-xs"
                                        >
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {c.excel.rows.map((row, i) => (
                                    <tr key={i}>
                                        {row.map((cell, j) => (
                                            <td
                                                key={j}
                                                className={`border border-zinc-200 px-3 py-2 ${
                                                    j === 0
                                                        ? "font-mono text-emerald-700"
                                                        : ""
                                                }`}
                                            >
                                                {cell}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Troubleshooting */}
                <section className="help-card border border-zinc-200 bg-white p-5 sm:p-6 mb-5">
                    <h2 className="text-lg font-bold text-zinc-900 mb-3">
                        {c.trouble.h}
                    </h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-zinc-200">
                            <tbody>
                                {c.trouble.rows.map((row, i) => (
                                    <tr key={i}>
                                        <td className="border border-zinc-200 px-3 py-2 font-semibold w-1/3 bg-rose-50 text-rose-900">
                                            {row[0]}
                                        </td>
                                        <td className="border border-zinc-200 px-3 py-2 text-zinc-700">
                                            ✅ {row[1]}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Support */}
                <section className="help-card border border-zinc-200 bg-gradient-to-br from-zinc-900 to-violet-900 text-white p-6 mb-8">
                    <h2 className="text-xl font-bold mb-3">{c.support}</h2>
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                        <a
                            href="mailto:support@idcardgen.com"
                            className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 px-4 py-3 flex items-center gap-2"
                        >
                            📧 support@idcardgen.com
                        </a>
                        <a
                            href="https://wa.me/917582896874"
                            target="_blank"
                            rel="noreferrer"
                            className="bg-emerald-500/20 hover:bg-emerald-500/30 backdrop-blur border border-emerald-400/30 px-4 py-3 flex items-center gap-2"
                        >
                            💬 WhatsApp:{" "}
                            {lang === "hi" ? "Chat karein" : "Chat now"}
                        </a>
                    </div>
                </section>

                {/* Footer */}
                <p className="text-xs text-zinc-400 text-center">
                    © IDCardGen {new Date().getFullYear()} ·{" "}
                    {lang === "hi"
                        ? "Sarvadhikar surakshit"
                        : "All rights reserved"}
                </p>
            </div>
        </>
    );
}
